import { CalendarClock, FilePlus2, LayoutDashboard, ListChecks, ShieldCheck, User, Wallet } from "lucide-react";
import { Navigate, useLocation } from "react-router-dom";
import AppShell from "@/layouts/AppShell";
import WalletBadge from "@/pages/retailer/wallet/components/WalletBadge";
import KycBanner from "@/pages/retailer/kyc/components/KycBanner";
import {
  RetailerSessionProvider,
  useRetailerSession,
} from "@/components/feature/retailer/RetailerSessionContext";
import { getSession, isStaffSession } from "@/components/feature/auth/session";

function getBreadcrumb(path: string): string {
  if (path === "/retailer/dashboard") return "Dashboard";
  if (path.startsWith("/retailer/kyc")) return "KYC";
  if (path.startsWith("/retailer/create-mandate")) return "Create Mandate";
  if (path.startsWith("/retailer/mandates")) return "My Mandates / Details";
  if (path.startsWith("/retailer/installments")) return "Installments";
  if (path.startsWith("/retailer/wallet")) return "Wallet";
  if (path.startsWith("/retailer/profile")) return "Profile";
  return "Dashboard";
}

function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export default function RetailerLayout() {
  const session = getSession();

  // If not logged in -> redirect to login
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // If staff user (super_admin, admin, retailer_onboarding_executive, retailer_onboarding_executive_walletrequesthandler, wallet_request_handler) -> redirect to /admin
  if (isStaffSession(session)) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <RetailerSessionProvider>
      <RetailerShell />
    </RetailerSessionProvider>
  );
}

function RetailerShell() {
  const location = useLocation();
  const { profile, kycStatus, isKycApproved } = useRetailerSession();

  const kycPending = kycStatus === "NOT_STARTED" || kycStatus === "DRAFT";

  const navItems = [
    { to: "/retailer/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/retailer/kyc", label: "KYC", icon: ShieldCheck, dot: false },
    { to: "/retailer/create-mandate", label: "Create Mandate", icon: FilePlus2 },
    { to: "/retailer/mandates", label: "My Mandates", icon: ListChecks },
    { to: "/retailer/installments", label: "Installments", icon: CalendarClock },
    { to: "/retailer/wallet", label: "Wallet", icon: Wallet },
    { to: "/retailer/profile", label: "Profile", icon: User },
  ];

  return (
    <AppShell
      navItems={navItems}
      user={{
        name: profile?.name ?? "Retailer",
        role: profile?.shopName ?? "Shop",
        initials: profile?.name ? initialsOf(profile.name) : "RT",
      }}
      breadcrumb={getBreadcrumb(location.pathname)}
      topbarExtra={<WalletBadge />}
      topBanner={undefined}
      createMandateDisabled={!isKycApproved}
    />
  );
}
